import { showDialogWithData } from "../../../../redux/guest-slices/other.slice"
import { setCurrectChat, updateCurrentChatAppointmentStatus } from "../../../../redux/user-slices/socket.slice"
import { Box, Grid2 as Grid, Paper, Stack, Typography } from "@mui/material"
import { useEffect, useState, useRef, useCallback, useMemo } from "react"
import { DAILY_CHAT_WARN } from "../../../../utils/keywords"
import UserServices from "../../../../services/user.service"
import { checkUserType, BASEURI, headers } from "../../../../utils/helper"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import Postview from "./files/postview"
import { Cookies } from "react-cookie"
import axios from "axios"
import moment from "moment"
import FooterApt from "./footerApt"
import Concent from "./concent"
import Message from "./message"
import Header from "./header"
import Footer from "./footer"
import { playReceiveMessageSound } from "../../../../utils/sound.utils"

const userServices = new UserServices()
const cookies = new Cookies()
function Chat({ isAppointmentChat = false }) {
  const listRef = useRef(null)
  const chatContainerRef = useRef(null)
  const dispatch = useDispatch()
  const lastFetchedPage = useRef(0)
  const isInitialLoad = useRef(true)
  const [data, setData] = useState({})
  const previousScrollHeight = useRef(0)
  const { state, pathname } = useLocation()
  const [isLoading, setIsLoading] = useState(false);

  const { socket, chat, appointmentAvailability } = useSelector((state) => state.socketSlice)
  const { specialist } = useSelector((state) => state.authSlice)
  const { subscription } = useSelector((state) => state.subscriptionSlice);

  const [chatUsage, setChatUsage] = useState({
    chatMessagesUsed: 0,
    totalChatMessages: 0,
    remainingChatMessages: 0,
  });
  const [chatLimitMessage, setChatLimitMessage] = useState("");

  const handleShowWarning = () => {
    const found = cookies.get("dailyChatWarn")
    if (!found) dispatch(showDialogWithData({ type: DAILY_CHAT_WARN }))
  }

  useEffect(() => {
    if (chat?._id && chatContainerRef.current) {
      chatContainerRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [chat?._id]);

  const [nowTick, setNowTick] = useState(Date.now());
  const currentAvailability = data?.availability || appointmentAvailability;

  const availabilityStatus = useMemo(() => {
    if (!currentAvailability?.startTime || !currentAvailability?.endTime || !currentAvailability?.timeZone) {
      return "invalid";
    }

    const tz = currentAvailability.timeZone || currentAvailability.timezone;
    const start = moment(currentAvailability.startTime).tz(tz);
    const end = moment(currentAvailability.endTime).tz(tz);
    const now = moment().tz(tz);

    if (now.isBefore(start)) {
      return "before";
    }
    if (now.isAfter(end)) {
      return "after";
    }
    return "during";
  }, [currentAvailability, nowTick]);

  // Memoized getMessages function
  const getMessages = useCallback(async (roomId, search = "", limit = 20, currentPage = 1) => {
    if (isAppointmentChat) {
      try {
        const response = await axios.get(`${BASEURI}/user/appointment-chat/messages/${roomId}`, {
          headers: headers()
        });
        if (response.data?.success) {
          const messages = response.data.data;
          
          // Mark as read in backend
          await axios.put(`${BASEURI}/user/appointment-chat/read/${roomId}`, {}, {
            headers: headers()
          });

          const formatted = {
            pagination: {
              currentPage: 1,
              hasNextPage: false,
              totalCount: messages.length,
              totalPages: 1
            },
            records: messages,
            availability: null
          };
          
          setData(formatted);
          setIsLoading(false);
          isInitialLoad.current = false;

          // Join the room for real-time updates
          if (socket) {
            socket.emit("join-room", roomId);
          }
        }
      } catch (error) {
        console.error("Error fetching appointment messages:", error);
        setIsLoading(false);
      }
    } else {
      if (!socket) return
      socket.emit("get-messages", {
        roomId,
        search,
        limit,
        currentPage
      })
    }
  }, [socket, isAppointmentChat])

  // Load initial messages when chat changes
  useEffect(() => {
    if (chat?._id && socket) {
      setData({})
      setIsLoading(true)
      isInitialLoad.current = true
      lastFetchedPage.current = 0
      setChatLimitMessage("")
      setChatUsage({ chatMessagesUsed: 0, totalChatMessages: 0, remainingChatMessages: 0 })
      socket.emit("join-room", chat._id)
      getMessages(chat._id, "", 20, 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?._id, socket])

  // Infinite scroll - load more messages when scrolling to top
  useEffect(() => {
    const listElement = listRef.current
    if (!listElement) return

    let isScrolling = false

    const handleScroll = () => {
      // Prevent multiple simultaneous calls
      if (isScrolling || isLoading || !data?.pagination?.hasNextPage) {
        return
      }

      // Check if scrolled near the top (within 100px)
      if (listElement.scrollTop <= 100) {
        isScrolling = true
        setIsLoading(true)

        // Store current scroll height and position
        previousScrollHeight.current = listElement.scrollHeight

        // Load next page
        getMessages(chat?._id, "", 20, data?.pagination?.currentPage + 1)

        // Reset flag after a delay to prevent rapid firing
        setTimeout(() => {
          isScrolling = false
        }, 1000)
      }
    }

    listElement.addEventListener("scroll", handleScroll)
    return () => {
      listElement.removeEventListener("scroll", handleScroll)
    }
  }, [chat?._id, data?.pagination, isLoading, getMessages])

  // Socket event listeners
  useEffect(() => {
    if (!socket) return
    handleShowWarning()

    const handleMessagesList = ({ data: responseData }) => {
      const currentPage = responseData?.pagination?.currentPage

      // Prevent processing duplicate responses for the same page
      if (currentPage === lastFetchedPage.current) {
        return
      }

      lastFetchedPage.current = currentPage

      // Log message IDs or timestamps to debug

      const formatted = {
        pagination: { ...responseData?.pagination },
        records: [...responseData?.records?.reverse()],
        availability: { ...responseData?.availability },
      }

      setData(prev => {
        // If it's the first page, replace data
        if (currentPage === 1) {
          return formatted
        }

        // If loading more (pagination), prepend OLDER messages to the TOP
        const updated = {
          pagination: formatted.pagination,
          records: [...formatted.records, ...(prev?.records || [])]
        }
        return updated
      })

      setIsLoading(false)

      // Restore scroll position after loading more messages (not initial load)
      if (responseData?.pagination?.currentPage > 1 && listRef.current) {
        setTimeout(() => {
          const listElement = listRef.current
          if (listElement) {
            const newScrollHeight = listElement.scrollHeight
            const scrollDifference = newScrollHeight - previousScrollHeight.current
            listElement.scrollTop = scrollDifference
          }
        }, 50)
      }

      // Scroll to bottom only on initial load
      if (isInitialLoad.current && listRef.current) {
        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight
            isInitialLoad.current = false
          }
        }, 100)
      }
    }

    socket.on("messages-list", handleMessagesList)

    socket.on("receive-message", ({ message }) => {
      playReceiveMessageSound();

      setData(prev => {
        if (prev?.records?.some(r => r._id === message._id)) return prev;
        return {
          ...prev,
          records: [...(prev?.records || []), message]
        };
      })

      // Auto-scroll to bottom on new message if already near bottom
      setTimeout(() => {
        if (listRef.current) {
          const listElement = listRef.current
          const isNearBottom = listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight < 100

          if (isNearBottom) {
            listElement.scrollTop = listElement.scrollHeight
          }
        }
      }, 50)
    })

    socket.on("message-sent", ({ message }) => {
      setData(prev => {
        if (prev?.records?.some(r => r._id === message._id)) return prev;
        return {
          ...prev,
          records: [...(prev?.records || []), message]
        };
      })

      // Always scroll to bottom on sent message
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight
        }
      }, 50)
    })

    socket.on(
      "chat-usage-updated",
      ({
        chatMessagesUsed,
        totalChatMessages,
        remainingChatMessages,
      }) => {
        setChatUsage({
          chatMessagesUsed,
          totalChatMessages,
          remainingChatMessages,
        });
      }
    );

    socket.on(
      "chat-limit-reached",
      ({ message }) => {
        setChatLimitMessage(message);

        // dispatch(
        //   showDialogWithData({
        //     type: "CHAT_LIMIT_REACHED",
        //     data: {
        //       message,
        //     },
        //   })
        // );
      }
    );

    socket.on(
      "appointment-completed-by-member",
      ({ appointmentId, appointmentStatus }) => {
        dispatch(
          updateCurrentChatAppointmentStatus({
            appointmentId,
            status: appointmentStatus || "completed",
          })
        );
      }
    );
    return () => {
      socket.off("message-sent")
      socket.off("receive-message")
      socket.off("messages-list", handleMessagesList)
      socket.off("chat-usage-updated");
      socket.off("chat-limit-reached");
      socket.off("appointment-completed-by-member");
    }
    // eslint-disable-next-line
  }, [socket])

  useEffect(() => {
    if (state !== "test" && pathname.includes("messages"))
      dispatch(setCurrectChat(state))
    // eslint-disable-next-line
  }, [state])

  return (
    <Grid size={{ xs: 12, md: "grow" }}>
      <Paper
        ref={chatContainerRef}
        sx={{
          p: 0,
          display: "grid",
          height: { xs: "520px", md: "580px" },
          maxHeight: "calc(100vh - 200px)",
          minHeight: "450px",
          gridTemplateRows: "auto 1fr auto"
        }}
        variant="outlined"
      >
        {Boolean(Object.keys(chat || {})?.length) ? (
          <>
            <Header availability={data?.availability} isAppointmentChat={isAppointmentChat} />
            <Stack
              p={1}
              ref={listRef}
              spacing={1.5}
              bgcolor="#f0f0f0"
              className="scrollbar-hidden"
              sx={{
                overflowY: "auto",
                height: "100%",
                minHeight: 0
              }}
            >
              {isLoading && data?.pagination?.currentPage > 1 && (
                <Box sx={{ textAlign: "center", py: 1, color: "#666" }}>
                  Loading more messages...
                </Box>
              )}

              {Boolean(data?.records?.length) ?
                (chat?.status === "pending" && (specialist?.specialist?._id || specialist?._id)) ?
                  <Concent /> :
                  data?.records?.map((element, i) => (
                    element?.files?.length ?
                      <Postview
                        socket={socket}
                        message={element}
                        key={element?._id || i}
                        isSender={element?.sender === userServices?.getUser?.id}
                      /> :
                      <Message
                        socket={socket}
                        message={element}
                        key={element?._id || i}
                        isSender={element?.sender === userServices?.getUser?.id}
                      />
                  ))
                : !isLoading ? (
                  (isAppointmentChat || chat?.type === "appointment" || chat?.conversation_type === "appointment") && availabilityStatus === "before" ? (
                    <CountdownTimer
                      startTime={currentAvailability?.startTime}
                      timeZone={currentAvailability?.timeZone || currentAvailability?.timezone}
                      onCountdownEnd={() => setNowTick(Date.now())}
                    />
                  ) : (
                    <Box
                      sx={{
                        fontSize: 20,
                        display: "grid",
                        fontWeight: "500",
                        placeContent: "center",
                      }}
                    >No messages yet.
                    </Box>
                  )
                ) : null
              }
            </Stack>

            {checkUserType() === "member" &&
              !isAppointmentChat &&
              chat?.type !== "appointment" &&
              chat?.conversation_type !== "appointment" &&
              subscription?.plan?.name ===
              "premium" && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderTop: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    Messages Used:
                    {" "}
                    {chatUsage.chatMessagesUsed}/
                    {chatUsage.totalChatMessages}
                  </Box>

                  {/* <Box
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    Remaining:
                    {" "}
                    {chatUsage.remainingChatMessages}
                    {" "}
                    messages this month
                  </Box> */}
                </Box>
              )}

            {chat?.type === "general" ?
              <Footer availability={data?.availability} chatLimitMessage={chatLimitMessage}
                chatUsage={chatUsage} /> :
              <FooterApt availability={data?.availability} isAppointmentChat={isAppointmentChat} />
            }
          </>
        ) : (
          <>
            <Box />
            <Box
              sx={{
                fontSize: 20,
                display: "grid",
                fontWeight: "500",
                bgcolor: "#f0f0f0",
                placeContent: "center",
              }}
            >
              Select a chat
            </Box>
          </>
        )}
      </Paper>
    </Grid>
  )
}

function CountdownTimer({ startTime, timeZone, onCountdownEnd }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!startTime) return;

    const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const target = moment(startTime).tz(tz);

    const updateTimer = () => {
      const now = moment().tz(tz);
      const diff = target.diff(now);

      if (diff <= 0) {
        setTimeLeft("00:00:00");
        if (onCountdownEnd) onCountdownEnd();
        return;
      }

      const duration = moment.duration(diff);
      const hours = Math.floor(duration.asHours()).toString().padStart(2, "0");
      const minutes = duration.minutes().toString().padStart(2, "0");
      const seconds = duration.seconds().toString().padStart(2, "0");

      setTimeLeft(`${hours}:${minutes}:${seconds}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [startTime, timeZone]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        p: 3,
        my: "auto",
        textAlign: "center"
      }}
    >
      <Typography variant="h6" fontWeight="600" color="text.secondary">
        Appointment Starts In:
      </Typography>
      <Typography
        variant="h4"
        fontWeight="800"
        color="primary.main"
        sx={{
          fontFamily: "monospace",
          letterSpacing: "2px",
          bgcolor: "#fff",
          px: 3,
          py: 1.5,
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.05)"
        }}
      >
        {timeLeft || "Calculating..."}
      </Typography>
    </Box>
  );
}

export default Chat